import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { use$ } from "@legendapp/state/react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import ListConstants from "core/shared/components/list/ListConstants";
import EmptyRow from "core/shared/components/list/EmptyRow";
import useColumnHeader from "core/shared/hooks/useColumnHeader";
import DataExportListStore$ from "reports/data-export/data-export-list/stores/DataExportListStore";
import ReportsMessages from "reports/intl/ReportsMessages";
import ReportName from "reports/data-export/data-export-list/components/columns/ReportName";
import ReportType from "reports/data-export/data-export-list/components/columns/ReportType";
import ReportDateRange from "reports/data-export/data-export-list/components/columns/ReportDateRange";
import Header from "reports/data-export/data-export-list/components/header/Header";
import ContextMenu from "reports/data-export/data-export-list/components/columns/ContextMenu";
import DownloadBtn from "reports/data-export/data-export-list/components/columns/DownloadBtn";
import Footer from "reports/data-export/data-export-list/components/footer/Footer";
import StatusFilter from "reports/shared/components/list/filters/StatusFilter";
import ReportStatus from "reports/data-export/data-export-list/components/columns/ReportStatus";
import ReportTypeFilter from "reports/data-export/data-export-list/components/filters/ReportTypeFilter";
import NameFilter from "audience/shared/components/list/filters/NameFilter";

const { TABLE_HEADER_HEIGHT } = ListConstants;

const contextStyle = { width: "1rem" };
const reportNameStyle = { maxWidth: "2rem" };
const reportStatusStyle = { width: "2rem" };
const reportTypeStyle = { width: "25rem" };
const dateRangeStyle = { width: "18rem" };
const selectStyle = { width: "1rem" };
const tableStyle = { minWidth: "1080px" }; // ensures horizontal scroll

const StTableContainer = styled.div`
  width: 100%;
  height: calc(100vh - ${TABLE_HEADER_HEIGHT}px);
`;

const handleFilter = (evt) => {
  DataExportListStore$.tableState.assign({ filters: evt.filters, first: 0 });
};

const handleSelection = ({ value }) => {
  DataExportListStore$.selection.set(value);
};

const Table = ({ isLoading }) => {
  const { formatHeader } = useColumnHeader();
  const tableRef = useRef(null);

  return (
    <StTableContainer>
      <DataTable
        customprop={tableRef}
        dataKey="id"
        emptyMessage={EmptyRow}
        filterDelay={1000}
        filterDisplay="row"
        filters={use$(DataExportListStore$.tableState.filters)}
        first={use$(DataExportListStore$.tableState.first)}
        footer={Footer}
        header={Header}
        lazy
        loading={isLoading}
        onFilter={handleFilter}
        onSelectionChange={handleSelection}
        ref={tableRef}
        rows={use$(DataExportListStore$.tableState.rows)}
        rowHover
        scrollable
        scrollHeight="flex"
        selection={use$(DataExportListStore$.selection)}
        selectionPageOnly
        size="small"
        sortField={use$(DataExportListStore$.tableState.sortField)}
        sortOrder={use$(DataExportListStore$.tableState.sortOrder)}
        tableStyle={tableStyle}
        totalRecords={use$(DataExportListStore$.totalRecords)}
        value={use$(DataExportListStore$.tableData)}
      >
        <Column headerStyle={selectStyle} selectionMode="multiple" />
        <Column
          body={ReportName}
          field="reportName"
          // Note: report api doesn't currently support filtering by name
          // filter
          filterElement={NameFilter}
          header={formatHeader(ReportsMessages.reportName)}
          showFilterMenu={false}
          style={reportNameStyle}
        />
        <Column
          body={ReportStatus}
          field="status"
          filter
          filterElement={StatusFilter}
          header={formatHeader(ReportsMessages.reportStatus)}
          showFilterMenu={false}
          style={reportStatusStyle}
        />
        <Column
          body={ReportType}
          field="reportType"
          filter
          filterElement={ReportTypeFilter}
          header={formatHeader(ReportsMessages.reportType)}
          showFilterMenu={false}
          style={reportTypeStyle}
        />
        <Column
          body={ReportDateRange}
          field="dateRange"
          header={formatHeader(ReportsMessages.dateRange)}
          showFilterMenu={false}
          style={dateRangeStyle}
        />
        <Column body={DownloadBtn} style={contextStyle} />
        <Column body={ContextMenu} style={contextStyle} />
      </DataTable>
    </StTableContainer>
  );
};

Table.propTypes = {
  isLoading: PropTypes.bool,
};

export default Table;
